import { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import type { WireArticleBlock, WireImageArtifact, WireVisualArtifact, WireVideoArtifact, WireCaptionsArtifact, WireAudioArtifact } from '../../api/types'
import { TableBlockPanel } from './TableBlockPanel'
import { PhotoBlockPanel } from './PhotoBlockPanel'
import { PhotoGroupBlockPanel } from './PhotoGroupBlockPanel'
import { PhotoComparisonBlockPanel } from './PhotoComparisonBlockPanel'
import { VisualBlockPanel } from './VisualBlockPanel'
import { VideoBlockPanel } from './VideoBlockPanel'
import { AudioBlockPanel } from './AudioBlockPanel'
import { CmtWrapper } from '../common/cmt'
import type { CmtResult } from '../common/cmt'

// ─── Block type labels ────────────────────────────────────────────────────────

const BLOCK_TYPE_LABELS: Record<WireArticleBlock['type'], string> = {
  'classification-banner': 'Classification Banner',
  'heading':               'Heading',
  'paragraph':             'Paragraph',
  'section-marker':        'Section Marker',
  'report-details':        'Report Details',
  'attribution':           'Attribution',
  'visual':                'Visual',
  'list':                  'List',
  'textbox':               'Textbox',
  'warning':               'Warning',
  'source-list':           'Source List',
  'table':                 'Table',
  'photo':                 'Photo',
  'photo-group':           'Photo Group',
  'photo-comparison':      'Photo Comparison',
  'video':                 'Video',
  'audio':                 'Audio',
}

// ─── Section type labels ──────────────────────────────────────────────────────

const SECTION_LABELS: Record<string, string> = {
  'summary':            'Key Judgments',
  'scope-note':         'Scope Note',
  'methodology-note':   'Methodology Note',
  'tradecraft-summary': 'Tradecraft Summary',
  'for-additional-info':'For Additional Info',
  'appendix':           'Appendix',
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function MetaRow({ label, value }: { label: string; value: string | undefined }) {
  if (value === undefined) return null
  return (
    <div>
      <p className="text-[9px] font-bold uppercase tracking-widest text-fg-secondary">{label}</p>
      <p className="mt-0.5 text-xs text-fg">{value}</p>
    </div>
  )
}

// ─── Block-specific detail fields ────────────────────────────────────────────

function BlockDetail({ block }: { block: WireArticleBlock }) {
  switch (block.type) {
    case 'heading':
      return <MetaRow label="Level" value={`H${block.level}`} />

    case 'section-marker':
      return <MetaRow label="Section Type" value={SECTION_LABELS[block.sectionType] ?? block.sectionType.replace(/-/g, ' ')} />

    case 'list':
      return (
        <>
          <MetaRow label="Style" value={block.style === 'unordered' ? 'Bulleted' : 'Numbered'} />
          <MetaRow label="Items" value={String(block.items.length)} />
        </>
      )

    case 'table':
      return (
        <>
          <MetaRow label="Columns" value={String((block.rows.find(r => r.rowType === 'body') ?? block.rows[0])?.cells?.length ?? 0)} />
          <MetaRow label="Rows" value={String(block.rows.length)} />
          {block.caption && <MetaRow label="Caption" value={block.caption} />}
        </>
      )

    case 'visual':
      // Full editing panel rendered separately; nothing to show here.
      return null

    case 'attribution':
      return (
        <>
          {block.producingOffice && <MetaRow label="Office" value={block.producingOffice} />}
          {block.authors && block.authors.length > 0 && (
            <MetaRow label="Authors" value={block.authors.join(', ')} />
          )}
          {block.sourceTypes && block.sourceTypes.length > 0 && (
            <MetaRow label="Source Types" value={block.sourceTypes.join(', ')} />
          )}
        </>
      )

    case 'report-details':
      return (
        <>
          {block.documentId && <MetaRow label="Document ID" value={block.documentId} />}
          {block.publishedAt && (
            <MetaRow
              label="Published"
              value={new Date(block.publishedAt).toLocaleDateString('en-US', { dateStyle: 'long' })}
            />
          )}
          {block.topics && block.topics.length > 0 && (
            <MetaRow label="Topics" value={block.topics.join(', ')} />
          )}
        </>
      )

    case 'textbox':
      return <MetaRow label="Children" value={`${block.children.length} block${block.children.length !== 1 ? 's' : ''}`} />

    case 'source-list':
      return <MetaRow label="Entries" value={String(block.entries.length)} />

    case 'photo':
      // Full editing panel rendered separately; nothing to show here.
      return null

    case 'photo-group':
      // Full editing panel rendered separately; nothing to show here.
      return null

    case 'photo-comparison':
      // Full editing panel rendered separately; nothing to show here.
      return null

    case 'video':
      // Full editing panel rendered separately; nothing to show here.
      return null

    case 'audio':
      // Full editing panel rendered separately; nothing to show here.
      return null

    default:
      return null
  }
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  selectedBlock: WireArticleBlock | null
  onClearSelection: () => void
  onSaveBlockMeta: (blockId: string, patch: { classification: string; disseminationControls?: string[] }) => void
  onUpdateBlock: (blockId: string, block: WireArticleBlock) => void
  imageArtifacts: WireImageArtifact[]
  visualArtifacts: WireVisualArtifact[]
  videoArtifacts: WireVideoArtifact[]
  captionsArtifacts: WireCaptionsArtifact[]
  audioArtifacts: WireAudioArtifact[]
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ArticleBlockInspector({ selectedBlock, onClearSelection, onSaveBlockMeta, onUpdateBlock, imageArtifacts, visualArtifacts, videoArtifacts, captionsArtifacts, audioArtifacts }: Props) {
  const [cmtResult, setCmtResult] = useState<CmtResult | undefined>(
    () => selectedBlock?.classification ? { classification: selectedBlock.classification, disseminationControls: selectedBlock.disseminationControls ?? [] } : undefined
  )

  // Reset classification widget whenever a different block is selected
  useEffect(() => {
    setCmtResult(
      selectedBlock?.classification
        ? { classification: selectedBlock.classification, disseminationControls: selectedBlock.disseminationControls ?? [] }
        : undefined
    )
  }, [selectedBlock?.id])

  function handleCmtChange(result: CmtResult) {
    setCmtResult(result)
    if (!selectedBlock) return
    onSaveBlockMeta(selectedBlock.id, { classification: result.classification, disseminationControls: result.disseminationControls.length ? result.disseminationControls : undefined })
  }

  // ── Nothing selected ─────────────────────────────────────────────────────────

  if (!selectedBlock) {
    return (
      <aside className="flex w-10 shrink-0 flex-col items-center border-l border-line bg-shelf py-3">
        {/* Intentionally empty — visual balance with icon rail on the left */}
      </aside>
    )
  }

  // ── Block selected ────────────────────────────────────────────────────────────

  return (
    <aside className="w-64 shrink-0 overflow-y-auto border-l border-line bg-shelf p-4">

      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <p className="text-[9px] font-bold uppercase tracking-widest text-fg-secondary">
          Block
        </p>
        <button
          type="button"
          onClick={onClearSelection}
          className="rounded p-0.5 text-fg-secondary transition-colors hover:bg-fill hover:text-fg"
          aria-label="Clear selection"
        >
          <X size={13} />
        </button>
      </div>

      <div className="space-y-4">

        {/* Block type */}
        <div>
          <p className="text-[9px] font-bold uppercase tracking-widest text-fg-secondary">Type</p>
          <p className="mt-0.5 text-xs font-semibold text-fg">
            {BLOCK_TYPE_LABELS[selectedBlock.type] ?? selectedBlock.type}
          </p>
        </div>

        {/* Classification / portion mark — editable */}
        <div>
          <p className="mb-1 text-[9px] font-bold uppercase tracking-widest text-fg-secondary">
            Portion Mark
          </p>
          <CmtWrapper value={cmtResult} onChange={handleCmtChange} compact />
        </div>

        {/* Block-specific fields */}
        {selectedBlock.type === 'visual' ? (
          <VisualBlockPanel
            block={selectedBlock}
            onUpdate={(updated) => onUpdateBlock(selectedBlock.id, updated)}
            visualArtifacts={visualArtifacts}
          />
        ) : selectedBlock.type === 'photo' ? (
          <PhotoBlockPanel
            block={selectedBlock}
            onUpdate={(updated) => onUpdateBlock(selectedBlock.id, updated)}
            imageArtifacts={imageArtifacts}
          />
        ) : selectedBlock.type === 'photo-group' ? (
          <PhotoGroupBlockPanel
            block={selectedBlock}
            onUpdate={(updated) => onUpdateBlock(selectedBlock.id, updated)}
            imageArtifacts={imageArtifacts}
          />
        ) : selectedBlock.type === 'photo-comparison' ? (
          <PhotoComparisonBlockPanel
            block={selectedBlock}
            onUpdate={(updated) => onUpdateBlock(selectedBlock.id, updated)}
            imageArtifacts={imageArtifacts}
          />
        ) : selectedBlock.type === 'table' ? (
          <TableBlockPanel
            block={selectedBlock}
            onUpdate={(updated) => onUpdateBlock(selectedBlock.id, updated)}
          />
        ) : selectedBlock.type === 'video' ? (
          <VideoBlockPanel
            block={selectedBlock}
            onUpdate={(updated) => onUpdateBlock(selectedBlock.id, updated)}
            videoArtifacts={videoArtifacts}
            imageArtifacts={imageArtifacts}
            captionsArtifacts={captionsArtifacts}
          />
        ) : selectedBlock.type === 'audio' ? (
          <AudioBlockPanel
            block={selectedBlock}
            onUpdate={(updated) => onUpdateBlock(selectedBlock.id, updated)}
            audioArtifacts={audioArtifacts}
            captionsArtifacts={captionsArtifacts}
          />
        ) : (
          <BlockDetail block={selectedBlock} />
        )}

        {/* Block ID */}
        <div className="border-t border-line pt-4">
          <p className="text-[9px] font-bold uppercase tracking-widest text-fg-secondary">Block ID</p>
          <p className="mt-0.5 truncate font-mono text-[10px] text-fg-secondary">
            {selectedBlock.id}
          </p>
        </div>

      </div>
    </aside>
  )
}
